// @flow

import * as React from 'react';

import { useParams } from 'react-router-dom';

import {
  contactbook,
  type FavoriteContact,
} from '@gandi/redux-resource-contactbook';
import { useTranslator } from '@gandi/translate';

import { useAppSelector } from '../../../../../hooks/useAppSelector';
import {
  selectCurrentContactForProcessAndRole,
  selectSetupOwner,
} from '../../../modules/selectors';
import type { CreateOrTransfer, EditableRole } from '../../../types';
import { DomainContactForm } from '../DomainContactForm';
import {
  formSection2,
  useDomainContactFields,
  visitFormNodes,
} from '../helpers';
import type { FormValues } from '../types';

export type StateProps = {
  initialValues: FormValues,
};

export type OwnProps = {
  canWriteContactBook: boolean,
  owner: void | FavoriteContact,
  // eslint-disable-next-line react/no-unused-prop-types
  process: CreateOrTransfer,
  // eslint-disable-next-line react/no-unused-prop-types
  role: EditableRole,
};

export type Props = {
  ...StateProps,
  ...OwnProps,
};

const Presenter = ({
  canWriteContactBook,
  initialValues,
  owner,
}: Props): React.Node => {
  const translator = useTranslator();
  const { __ } = translator;
  const orgId = useAppSelector(selectSetupOwner);

  const { process, role } = useParams<{
    cartId: string,
    process: CreateOrTransfer,
    role: EditableRole,
  }>();

  const contact = useAppSelector(
    state =>
      selectCurrentContactForProcessAndRole(state, {
        process,
        role,
      }) || owner,
  );
  const isWorking = useAppSelector(state =>
    contact && contact?.id
      ? contactbook.selectIsPatchingOne(state, orgId, contact.id)
      : contactbook.selectIsCreatingOne(state, orgId),
  );

  // Good candidate to memoization.
  const fields = useDomainContactFields(true);

  // Describes how the form will be displayed
  const formView = {
    name: 'form__root',
    type: 'form__root',
    children: [formSection2],
  };

  // In each form node, use the fields as default values.
  const decoratedFormView = visitFormNodes(formView, node => ({
    ...fields.find(field => field.name === node.name),
    ...node,
  }));

  return (
    <DomainContactForm
      mode="EditContact"
      key={decoratedFormView}
      canWriteContactBook={canWriteContactBook}
      formView={decoratedFormView}
      cancelLabel={__('Cancel')}
      submitLabel={__('Save')}
      isWorking={isWorking}
      initialValues={initialValues}
    />
  );
};

export default Presenter;
